import React, { useState } from "react";
import { resetPasswordRequest } from "../../services/Api/loginService";
import { useConnexionStore } from "../../stores/Layout/connexion";
import { showToastError } from "../../services/Toasts/toasts";

const ModalForgotPassword = () => {
  // Store
  const { showModalForgotPassword, setShowModalForgotPassword } =
    useConnexionStore();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSubmit(event);
    }
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setIsLoading(true);
    resetPasswordRequest(email)
      .then(() => {
        setShowModalForgotPassword(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response.data === "googleAccount") {
          showToastError(
            "Vous ne pouvez pas réinitialiser le mot de passe de votre compte Google. Merci d'utiliser la connexion avec Google."
          );
        } else if (err.response.data === "officeAccount") {
          showToastError(
            "Vous ne pouvez pas réinitialiser le mot de passe de votre compte Office. Merci d'utiliser la connexion avec Office."
          );
        } else if (err.response.data === "accountNotCreated") {
          showToastError("Vous n'avez pas créé de compte");
        } else if (err.response.data === "AccountNotFound") {
          showToastError("Aucun compte n'a été trouvé avec cette adresse mail");
        }
      });
  };

  if (!showModalForgotPassword) return null;

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-hidden focus:outline-hidden">
        <div className="relative w-auto md:w-1/4 my-6 mx-auto max-w-3xl min-w-[85%]">
          {/* Content */}
          <div className="border-0 rounded-xl shadow-lg relative flex flex-col w-full bg-white outline-hidden focus:outline-hidden">
            {/* Header */}
            <div className="w-full bg-white p-3 rounded-t-xl">
              <h4 className="text-xl font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <svg className="w-5 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Mot de passe oublié
              </h4>
            </div>
            
            {/* Body */}
            <div className="w-full flex flex-col items-center justify-center text-grey-trio">
              <div className="flex flex-col justify-center items-center w-11/12 mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2 text-center">
                  Réinitialisation du mot de passe
                </h3>
                <p className="text-md text-gray-600 text-center mb-4">
                  Veuillez saisir votre adresse email pour recevoir un lien de réinitialisation.
                </p>
                
                {/* Email input */}
                <div className="w-full space-y-2">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700 block text-left w-full">
                    Adresse email
                  </label>
                  <input
                    type="email"
                    className="w-full h-12 px-4 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-yellow-trio focus:bg-white focus:outline-none transition-all duration-300 shadow-sm hover:shadow-md text-gray-700 placeholder-gray-400"
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                    onChange={(e) => setEmail(e.target.value)}
                    minLength={10}
                    maxLength={200}
                    name="email"
                    id="email"
                    placeholder="votre-email@exemple.com"
                    onKeyDown={handleKeyDown}
                    value={email}
                    required
                  />
                </div>
                
                <p className="text-sm text-gray-500 text-center mt-3 italic">
                  Un email vous sera envoyé avec les instructions de réinitialisation.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-3 rounded-b-2xl bg-white gap-3">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-500 hover:bg-red-500 hover:text-white cursor-pointer font-semibold rounded-xl transition-all duration-200 flex items-center gap-2 text-sm"
                type="button"
                onClick={() => setShowModalForgotPassword(false)}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Annuler
              </button>
              <button
                className={`${isLoading ? "opacity-50 cursor-not-allowed" : "opacity-80 hover:opacity-100 cursor-pointer"} px-4 py-2 bg-yellow-trio hover:bg-yellow-400 cursor-pointer text-gray-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm`}
                type="submit"
                disabled={isLoading}
                onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  handleSubmit(event);
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {isLoading ? "Envoie..." : "Réinitialiser"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-45 fixed inset-0 z-40 bg-black"></div>
    </>
  );
};

export default ModalForgotPassword;
